import React, { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock, Zap, ShieldCheck, Users2, DollarSign, TrendingUp, AlertTriangle, RefreshCw, KeyRound } from "lucide-react";

function StatusBadge({ st }) {
  if (st.reason === "cancelled") return <span className="text-[10px] px-2 py-0.5 rounded bg-red-100 text-red-800 font-bold inline-flex items-center gap-1"><XCircle className="w-3 h-3" /> ملغى</span>;
  if (!st.active) return <span className="text-[10px] px-2 py-0.5 rounded bg-red-100 text-red-800 font-bold inline-flex items-center gap-1"><XCircle className="w-3 h-3" /> منتهي</span>;
  if (st.reason === "override") return <span className="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-bold inline-flex items-center gap-1"><KeyRound className="w-3 h-3" /> مفتوح يدوياً</span>;
  return <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> نشط</span>;
}

export default function Subscriptions() {
  const [rows, setRows] = useState([]);
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelNote, setCancelNote] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false);
  const [target, setTarget] = useState(null);
  const [assign, setAssign] = useState({ plan_id: "", months: 1, note: "" });
  const [override, setOverride] = useState({ days: 7, note: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [r, p, s] = await Promise.all([
        api.get("/subscriptions"), api.get("/plans"), api.get("/subscriptions/stats"),
      ]);
      setRows(r.data); setPlans(p.data); setStats(s.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openAssign = (row) => { setTarget(row); setAssign({ plan_id: plans[0]?.id || "", months: 1, note: "" }); setAssignOpen(true); };
  const openOverride = (row) => { setTarget(row); setOverride({ days: 7, note: "" }); setOverrideOpen(true); };

  const doAssign = async () => {
    if (!assign.plan_id) return toast.error("اختر خطة");
    try {
      await api.post(`/subscriptions/${target.company_id}/assign`, assign);
      toast.success(`تم تفعيل الاشتراك لـ ${target.company_name}`);
      setAssignOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const doOverride = async () => {
    const until = new Date(Date.now() + (+override.days || 0) * 24 * 60 * 60 * 1000).toISOString();
    try {
      await api.post(`/subscriptions/${target.company_id}/override`, { override_until: until, note: override.note });
      toast.success("تم فتح الحساب يدوياً");
      setOverrideOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const clearOverride = async (row) => {
    if (!confirm(`إلغاء الفتح اليدوي لشركة ${row.company_name}؟`)) return;
    try {
      await api.post(`/subscriptions/${row.company_id}/override`, { override_until: null });
      toast.success("تم إلغاء الفتح اليدوي"); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const openCancel = (row) => { setTarget(row); setCancelNote(""); setCancelOpen(true); };
  const doCancel = async () => {
    if (!target) return;
    setCancelBusy(true);
    try {
      await api.post(`/subscriptions/${target.company_id}/cancel`, { note: cancelNote.trim() });
      toast.success(`تم إلغاء اشتراك ${target.company_name}`);
      setCancelOpen(false);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setCancelBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="subscriptions-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-md bg-primary grid place-items-center text-primary-foreground"><Users2 className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl md:text-3xl font-heading font-bold">اشتراكات الشركات</h1>
          <p className="text-sm text-muted-foreground">إدارة اشتراكات جميع شركاتك المشتركة</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-1" data-testid="refresh-btn"><RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> تحديث</Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-4 border-emerald-200 bg-emerald-50">
            <div className="text-xs text-emerald-700">شركات نشطة</div>
            <div className="text-2xl font-bold text-emerald-900 mt-1">{stats.active}</div>
            <CheckCircle2 className="w-5 h-5 text-emerald-600 absolute" style={{position: "relative", marginTop: -30, marginInlineStart: "auto", float: "inline-end"}} />
          </Card>
          <Card className="p-4 border-amber-200 bg-amber-50">
            <div className="text-xs text-amber-700">تنتهي قريباً</div>
            <div className="text-2xl font-bold text-amber-900 mt-1">{stats.expiring_soon}</div>
          </Card>
          <Card className="p-4 border-red-200 bg-red-50">
            <div className="text-xs text-red-700">منتهية</div>
            <div className="text-2xl font-bold text-red-900 mt-1">{stats.expired}</div>
          </Card>
          <Card className="p-4 border-primary/20 bg-primary/5">
            <div className="text-xs text-primary">إجمالي الإيراد المسجل</div>
            <div className="text-2xl font-bold text-primary mt-1">${stats.total_revenue_recorded.toFixed(2)}</div>
            <div className="text-[10px] text-muted-foreground">{stats.payments_pending} دفعة بانتظار الاعتماد</div>
          </Card>
        </div>
      )}

      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>الشركة</TableHead>
              <TableHead>الحالة</TableHead>
              <TableHead>الخطة</TableHead>
              <TableHead>ينتهي في</TableHead>
              <TableHead>فتح يدوي حتى</TableHead>
              <TableHead className="text-center">إجراءات</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.company_id} data-testid={`sub-row-${r.company_id}`}>
                <TableCell className="font-bold">{r.company_name}</TableCell>
                <TableCell><StatusBadge st={r} /></TableCell>
                <TableCell className="text-sm">{r.plan_name || "—"}</TableCell>
                <TableCell className="text-xs font-mono" dir="ltr">{r.expires_at ? new Date(r.expires_at).toLocaleDateString("ar-EG") : "—"}</TableCell>
                <TableCell className="text-xs font-mono" dir="ltr">{r.override_until ? new Date(r.override_until).toLocaleDateString("ar-EG") : "—"}</TableCell>
                <TableCell>
                  <div className="flex gap-1 justify-center flex-wrap">
                    <Button size="sm" variant="outline" onClick={()=>openAssign(r)} className="gap-1 h-7 text-xs" data-testid={`assign-${r.company_id}`}><Zap className="w-3 h-3" /> تفعيل خطة</Button>
                    {r.override_until ? (
                      <Button size="sm" variant="outline" onClick={()=>clearOverride(r)} className="gap-1 h-7 text-xs text-destructive" data-testid={`clear-override-${r.company_id}`}>إلغاء الفتح</Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={()=>openOverride(r)} className="gap-1 h-7 text-xs text-amber-700 border-amber-400" data-testid={`override-${r.company_id}`}><KeyRound className="w-3 h-3" /> فتح يدوي</Button>
                    )}
                    {r.active && !r.sold && <Button size="sm" variant="outline" onClick={()=>openCancel(r)} className="gap-1 h-7 text-xs text-destructive border-destructive/40" data-testid={`cancel-sub-${r.company_id}`}><XCircle className="w-3 h-3" /> إلغاء الاشتراك</Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">لا توجد شركات</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* Assign plan dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent data-testid="assign-dialog">
          <DialogHeader><DialogTitle>تفعيل خطة — {target?.company_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>الخطة</Label>
              <Select value={assign.plan_id} onValueChange={(v)=>setAssign({...assign, plan_id: v})}>
                <SelectTrigger data-testid="assign-plan-select"><SelectValue placeholder="اختر خطة..." /></SelectTrigger>
                <SelectContent>{plans.map(p => <SelectItem key={p.id} value={p.id}>{p.name} — ${p.price_usd}/شهر</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>عدد الأشهر (0 = استخدم مدة الخطة)</Label><Input type="number" min="0" value={assign.months} onChange={(e)=>setAssign({...assign, months:+e.target.value})} data-testid="assign-months-input" /></div>
            <div><Label>ملاحظة (اختياري)</Label><Input value={assign.note} onChange={(e)=>setAssign({...assign, note:e.target.value})} data-testid="assign-note-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setAssignOpen(false)}>إلغاء</Button>
            <Button onClick={doAssign} data-testid="confirm-assign-btn">تفعيل</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Override dialog */}
      <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
        <DialogContent data-testid="override-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-amber-600" /> فتح الحساب يدوياً — {target?.company_name}</DialogTitle></DialogHeader>
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-2">
            هذا الخيار كندي 🎁 — يفتح حساب الشركة لمدة محددة حتى لو لم تدفع بعد. مفيد للفترات التجريبية أو مساعدة العملاء المؤقتة.
          </div>
          <div className="space-y-3">
            <div><Label>مدة الفتح (بالأيام)</Label><Input type="number" min="1" value={override.days} onChange={(e)=>setOverride({...override, days:+e.target.value})} data-testid="override-days-input" /></div>
            <div><Label>سبب الفتح (اختياري)</Label><Input value={override.note} onChange={(e)=>setOverride({...override, note:e.target.value})} placeholder="فترة تجريبية · تسهيل مؤقت..." data-testid="override-note-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOverrideOpen(false)}>إلغاء</Button>
            <Button onClick={doOverride} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-override-btn">فتح الحساب</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="cancel-subscription-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-destructive"><XCircle className="w-5 h-5" /> إلغاء اشتراك — {target?.company_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-destructive bg-red-50 border border-red-200 rounded px-3 py-3">
              سيُوقف الاشتراك فورًا، وتُلغى صلاحية الفتح اليدوي وميزات الخطة. ستبقى بيانات الشركة وسجل الاشتراكات والمدفوعات محفوظة. يمكن إعادة التفعيل لاحقًا بإسناد خطة أو اعتماد دفعة جديدة.
            </div>
            <div><Label>ملاحظة الإلغاء (اختياري)</Label><Input value={cancelNote} onChange={e=>setCancelNote(e.target.value)} placeholder="مثال: فُعّل الاشتراك بالخطأ" data-testid="cancel-subscription-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setCancelOpen(false)} disabled={cancelBusy}>رجوع</Button>
            <Button variant="destructive" onClick={doCancel} disabled={cancelBusy} data-testid="confirm-cancel-subscription">{cancelBusy ? "جارٍ الإلغاء…" : "تأكيد إلغاء الاشتراك الآن"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
